/* eslint-disable @typescript-eslint/no-unsafe-return, complexity -- TODO: fix unsafe return and reduce complexity */
import {
  FieldsCard,
  FieldsCardBody,
  FieldsData,
} from "@platform/design-system";
import {
  validateBetweenNumbers,
  validatePositiveNumber,
  validateZipCode,
} from "@platform/fe-utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { JSX, useState } from "react";
import { useApi } from "@platform/api";
import { useSignedInUser } from "@platform/user/client";

import { STATE_ABBREVIATION } from "../../../../constants";
import {
  getQueryKey,
  useRfpClientDetails,
} from "../../../../hooks/useRfpClientDetails";
import useToastNotificationService from "../../../../hooks/useToastNotificationService";
import { extractErrorMessage } from "../../../../utils";
import { buildFormattedAddress } from "../../helpers";

import { BenAdminOption, RfpClientResponse, TechCreditOption } from "./types";
import {
  formatRequestedSystem,
  formatTechCredit,
  parseRequestedSystemType,
  parseTechCreditType,
} from "./helpers";
import { BenAdminOptions, TechCreditOptions } from "./constants";

function validateTechCreditAmount(
  field: FieldsData,
  groupValues?: Record<string, unknown>,
): string | undefined {
  const creditType = groupValues?.techCreditType;
  if (
    (creditType === "percentage" || creditType === "pepm") &&
    Number(field.value) > 100
  ) {
    return "Amount cannot exceed 100.";
  }
  return undefined;
}

export function validateTechCreditType(
  field: FieldsData,
  groupValues?: Record<string, unknown>,
): string | undefined {
  if (
    groupValues?.techCreditNeeded === "yes" &&
    !field.value &&
    !field.inputValue
  ) {
    return "Please select a credit type.";
  }
  return undefined;
}

export type RfpClientPatchPayload = Record<
  string,
  string | number | boolean | readonly string[] | null | Record<string, unknown>
>;

export function buildRfpClientPayload(
  body: FieldsCardBody,
): RfpClientPatchPayload {
  const { requestedSystem, ...rest } = body;

  // requestedSystem is now a group: { system, other }
  const reqSystem = requestedSystem as Record<string, string> | null;
  const systemValue = reqSystem?.system;
  const otherValue = reqSystem?.other;

  const rfpAdditionalInfo = (rest.rfpAdditionalInfo || {}) as Record<
    string,
    string | number | null
  >;
  const { techCreditNeeded, ...restAdditionalInfo } = rfpAdditionalInfo;
  const isTechCreditNeeded = techCreditNeeded === "yes";
  const techCreditType = isTechCreditNeeded
    ? parseTechCreditType(restAdditionalInfo.techCreditType as string)
    : null;
  const isCarrierStandard = techCreditType === "carrierStandard";

  return {
    ...rest,
    hasInternationalEmployees: rest.hasInternationalEmployees === "yes",
    rfpAdditionalInfo: {
      ...restAdditionalInfo,
      technologyCreditNeeded: isTechCreditNeeded,
      requestedSystem: systemValue
        ? parseRequestedSystemType(systemValue)
        : null,
      requestedSystemOther:
        systemValue === "other" ? (otherValue ?? null) : null,
      techCreditType,
      techCreditAmount:
        !isTechCreditNeeded || isCarrierStandard
          ? null
          : (restAdditionalInfo.techCreditAmount ?? null),
    },
  };
}

type Props = { rfpId: string; refetchRpfDetails: VoidFunction };

function RFPClientCard({ rfpId, refetchRpfDetails }: Props): JSX.Element {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const [isEdit, setIsEdit] = useState(false);
  const { isBrokerOrAgent } = useSignedInUser();
  const { createToastNotification } = useToastNotificationService();

  const query = useRfpClientDetails({
    rfpId,
    select: mapResponseToFieldsCardData,
  });

  const mutation = useMutation<
    void,
    Error,
    Record<
      string,
      | string
      | number
      | boolean
      | readonly string[]
      | null
      | Record<string, unknown>
    >
  >({
    mutationFn: (data) =>
      axiosInstance
        .patch(`brokerportal/v1/rfp/${rfpId}/client`, data)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getQueryKey(rfpId) });
      handleCancel();
      refetchRpfDetails();
    },
    onError: (error) => {
      createToastNotification({
        message: extractErrorMessage(error),
        severity: "error",
      });
    },
  });

  const handleCancel = (): void => {
    setIsEdit(false);
  };

  const status = mutation.isPending ? "saving" : isEdit ? "edit" : query.status;
  const canEdit = !isBrokerOrAgent;

  const onSave = (body: FieldsCardBody): void => {
    if (body) mutation.mutate(buildRfpClientPayload(body));
    else handleCancel();
  };

  return (
    <FieldsCard
      title="Client"
      status={status}
      error={query.error}
      gridItemProps={{
        size: 3,
      }}
      skeleton={{
        count: 4,
        props: {
          height: 50,
        },
      }}
      onEdit={canEdit ? () => setIsEdit(true) : undefined}
      onCancel={canEdit ? handleCancel : undefined}
      onSave={canEdit ? onSave : undefined}
      data={query.data}
    />
  );
}

export function mapResponseToFieldsCardData(
  resp: RfpClientResponse,
): FieldsData[] {
  const address = resp.address;
  const arrAddress = buildFormattedAddress(address, {
    hasValidation: false,
  });
  const formattedAddress = arrAddress.filter(Boolean).join(", ");

  return [
    {
      label: "Name",
      value: resp.name,
      editable: true,
      inputType: "text",
      inputName: "name",
    },
    {
      label: "Address",
      visibleViewMode: !!address,
      value: formattedAddress,
      editable: true,
      inputType: "group",
      inputName: "address",
      inputProps: {
        fields: [
          {
            value: address?.addressLine1 ?? "",
            editable: true,
            inputType: "text",
            label: "Address Line 1",
            labelHiddenOnViewMode: true,
            hiddenOnEmptyViewMode: true,
            inputName: "addressLine1",
            gridItemProps: { size: 12 },
          },
          {
            value: address?.addressLine2 ?? "",
            editable: true,
            inputType: "text",
            label: "Address Line 2",
            labelHiddenOnViewMode: true,
            hiddenOnEmptyViewMode: true,
            inputName: "addressLine2",
            gridItemProps: { size: 12 },
          },
          {
            value: address?.city ?? "",
            editable: true,
            inputType: "text",
            inputName: "city",
            label: "City",
            labelHiddenOnViewMode: true,
            hiddenOnEmptyViewMode: true,
            gridItemProps: { size: 12 },
          },
          {
            value: address?.state ?? "",
            editable: true,
            inputType: "select",
            inputName: "state",
            gridItemProps: { size: 4 },
            label: "State",
            labelHiddenOnViewMode: true,
            hiddenOnEmptyViewMode: true,
            inputProps: {
              options: Object.entries(STATE_ABBREVIATION)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([stateName, abbreviation]) => ({
                  value: abbreviation,
                  label: `${stateName} - ${abbreviation}`,
                })),
            },
          },
          {
            value: address?.zipCode ?? "",
            editable: true,
            inputType: "text",
            inputName: "zipCode",
            label: "Zip Code",
            labelHiddenOnViewMode: true,
            hiddenOnEmptyViewMode: true,
            gridItemProps: { size: 8 },
            validators: [validateZipCode],
          },
        ],
        grid: {
          columns: 12,
          spacing: 1,
        },
      },
    },
    {
      label: "Situs State",
      value: resp.situsStateName || resp.situsState,
      editable: true,
      inputType: "select",
      inputName: "situsState",
      inputProps: {
        options: Object.entries(STATE_ABBREVIATION)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([stateName, abbreviation]) => ({
            value: abbreviation,
            label: `${stateName} - ${abbreviation}`,
          })),
      },
    },
    {
      label: "Client Type",
      value: resp.type,
    },
    {
      label: "Eligible Employees",
      value: resp.numberOfEmployees?.toString(),
      editable: true,
      inputType: "number",
      inputName: "numberOfEmployees",
      validators: [
        validatePositiveNumber,
        validateBetweenNumbers(1, 2_100_000_000),
      ],
    },
    {
      label: "Intl. Employees",
      value: resp.hasInternationalEmployees ? "Yes" : "No",
      editable: true,
      inputType: "select",
      inputName: "hasInternationalEmployees",
      inputValue: resp.hasInternationalEmployees ? "yes" : "no",
      inputProps: {
        helperText: "Are any employees working outside the U.S.?",
        options: [
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
        ],
      },
    },
    {
      label: "BenAdmin System",
      value: resp?.rfpAdditionalInfo?.requestedSystem
        ? formatRequestedSystem(
            resp?.rfpAdditionalInfo?.requestedSystem as BenAdminOption,
            resp?.rfpAdditionalInfo?.requestedSystemOther as string,
          )
        : "",
      editable: true,
      inputType: "group",
      inputName: "requestedSystem",
      inputProps: {
        fields: [
          {
            label: "BenAdmin System",
            editable: true,
            inputType: "select",
            inputName: "system",
            inputValue: resp?.rfpAdditionalInfo?.requestedSystem,
            value: resp?.rfpAdditionalInfo?.requestedSystem,
            inputProps: { options: BenAdminOptions },
            gridItemProps: { size: 12 },
          },
          {
            label: "Other System Name",
            editable: true,
            inputType: "text",
            inputName: "other",
            value: resp?.rfpAdditionalInfo?.requestedSystemOther ?? "",
            gridItemProps: { size: 12 },
            visibleWhen: (values) => values.system === "other",
          },
        ],
        grid: {
          columns: 12,
          spacing: 1,
        },
      },
    },
    {
      label: "Tech Credits",
      value: resp?.rfpAdditionalInfo?.techCreditType
        ? formatTechCredit(
            resp?.rfpAdditionalInfo?.techCreditType as TechCreditOption,
            resp?.rfpAdditionalInfo?.techCreditAmount as number,
          )
        : "No",
      editable: true,
      inputType: "group",
      inputName: "rfpAdditionalInfo",
      inputProps: {
        fields: [
          {
            label: "Tech Credits",
            editable: true,
            inputType: "select",
            inputName: "techCreditNeeded",
            inputProps: {
              options: [
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
              ],
            },
            inputValue: resp?.rfpAdditionalInfo?.technologyCreditNeeded
              ? "yes"
              : "no",
            value: resp?.rfpAdditionalInfo?.technologyCreditNeeded
              ? "yes"
              : "no",
            gridItemProps: { size: 12 },
          },
          {
            label: "Tech Credit Type",
            editable: true,
            inputType: "select",
            inputName: "techCreditType",
            validators: [validateTechCreditType],
            inputProps: {
              options: TechCreditOptions,
            },
            inputValue: resp?.rfpAdditionalInfo?.techCreditType,
            value: resp?.rfpAdditionalInfo?.techCreditType?.toLowerCase(),
            gridItemProps: { size: 12 },
            visibleWhen: (values) => values.techCreditNeeded === "yes",
          },
          {
            label: "Tech Credit Amount",
            editable: true,
            inputType: "number",
            inputName: "techCreditAmount",
            validators: [validatePositiveNumber, validateTechCreditAmount],
            value: resp?.rfpAdditionalInfo?.techCreditAmount?.toString(),
            inputProps: {
              type: "number",
              slotProps: {
                htmlInput: {
                  step: "0.01",
                  min: "0",
                  max:
                    resp?.rfpAdditionalInfo?.techCreditType === "percentage" ||
                    resp?.rfpAdditionalInfo?.techCreditType === "pepm"
                      ? "100"
                      : undefined,
                },
              },
            },
            gridItemProps: { size: 12 },
            visibleWhen: (values) =>
              values.techCreditNeeded === "yes" &&
              values.techCreditType !== "carrierStandard",
          },
        ],
        grid: {
          columns: 12,
          spacing: 1,
        },
      },
    },
  ];
}

export default RFPClientCard;
